import { Fragment, type ReactNode, useEffect, useState } from "react";

import * as PriceUtils from "~/utils/PriceUtils";
import Chip from "~/common/components/Chip/Chip";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { Link, LinkButton } from "~/common/components/Link";
import classNames from "classnames";

import { Table, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import LineItemDetails from "~/features/orders/common/components/ListItemDetails";
import { TBody, TCell, TUpperCell, TUpperRow } from "~/features/orders/common/components/TableElements";
import { type DocumentListRow } from "~/features/orders/orderList/useOrderListState";
import { getIsAllOpen } from "~/features/orders/orderList/OrderListPage";
import { OrderLineTable } from "~/features/orders/orderList/components/OrderLineTable";
import Spinner from "~/common/components/Spinner/Spinner";
import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import {
  ORDER_DATA_SORT_HANDLERS,
  OrderDataSort,
} from "~/features/orders/orderList/components/OrderListTable/orderDataSorting";
import {
  type HeaderCellData,
  useOrderListData,
} from "~/features/orders/orderList/components/OrderListTable/useOrderListData";
import { useTranslation } from "react-i18next";

import { isNonNull } from "~/common/utils/reducers";
import type { OrderData } from "~/domains/orders/types/modelsV2";
import { uniqueValues } from "~/common/utils/misc";
import { addStatusHover, isErrorEntry } from "~/features/orders/common/utils/util";
import { PopperTooltip } from "~/common/components/Tooltip";
import { DownloadContentButton } from "~/features/orders/quoteList/components/QuoteListTable/DownloadContentButton";
import { downloadFile } from "~/common/utils/ApiHelper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const ORDER_HISTORY_HEADER_DATA: HeaderCellData[] = [
  {
    title: "soNumber",
    subtitle: "",
    sortKind: OrderDataSort.SO,
  },
  {
    title: "poNumber",
    subtitle: "",
    sortKind: OrderDataSort.PO,
  },
  isMultiAccountOrderSearchEnabled() && {
    title: "account",
    subtitle: "endCustomer",
    sortKind: OrderDataSort.Account,
  },
  {
    title: "date",
    subtitle: "",
    sortKind: OrderDataSort.DatePlaced,
  },
  {
    title: "totalOnly",
    subtitle: "",
    sortKind: OrderDataSort.TotalPrice,
  },
  {
    title: "statusOnly",
    subtitle: "",
    sortKind: OrderDataSort.OrderStatus,
  },
].filter(isNonNull);

interface Props {
  rows: DocumentListRow<OrderData>[];
  className?: string;
  toggleOpen: (row: DocumentListRow<OrderData>) => void;
  toggleOpenAll: () => void;
}

export const OrderListTable = ({ rows, className, toggleOpen, toggleOpenAll }: Props) => {
  const { t } = useTranslation();
  const { sortedRows, renderHeaderCell } = useOrderListData({
    rows,
    sortHandlers: ORDER_DATA_SORT_HANDLERS,
    initialSortCode: OrderDataSort.DatePlaced,
    translationPrefix: "orderListPage.table.header.",
  });
  const [loading, setLoading] = useState({ id: "", state: false, hide: false });

  const isAllOpen = getIsAllOpen(sortedRows);

  const haveInvoices =
    sortedRows.filter(
      (item) => !isErrorEntry(item) && (item.data.orderStatusCode === "H5" || item.data.orderStatusCode === "H6"),
    )?.length > 0;

  const renderNumCells = (min: number) => {
    if (haveInvoices) {
      if (isMultiAccountOrderSearchEnabled()) {
        return min + 2;
      } else {
        return min + 1;
      }
    } else {
      if (isMultiAccountOrderSearchEnabled()) {
        return min + 1;
      } else {
        return min;
      }
    }
  };

  const handleDownload = (row: DocumentListRow<OrderData>) => {
    if (!row.open) {
      toggleOpen(row);
    }
    setLoading({ id: row.data.soNumber, state: true, hide: false });
  };

  const downloadInvoice = async (row: DocumentListRow<OrderData>) => {
    const urls = [];
    for (let i = 0; i < row.data.products.length; i++) {
      const data = row.data.products[i].invoiceData;
      if (data?.invoiceUrl && data?.invoiceNo) {
        urls.push(`${data.invoiceUrl}?id=${data.invoiceNo}&type=INVOICE`);
      }
    }
    uniqueValues(urls).forEach((url) => {
      downloadFile(url);
    });
  };

  useEffect(() => {
    if (loading.state) {
      const row = sortedRows.filter((row) => row.data.soNumber === loading.id)?.[0];
      if (!!row && row.detailsLoaded) {
        if (row.data.products.length > 0) {
          downloadInvoice(row).then(() => {
            setLoading({ id: row.data.soNumber, state: false, hide: false });
          });
        } else {
          setLoading({ id: loading.id, state: false, hide: true });
        }
      }
    }
  }, [sortedRows, loading.state]);

  const showDownloadPDF = (row: DocumentListRow<OrderData>) => {
    if (isErrorEntry(row)) {
      return false;
    }
    if (row.detailsLoaded) {
      const haveInvoices = row.data.products?.filter((item) => item.invoiceData?.invoiceUrl)?.length > 0;
      if ((row.data.orderStatusCode === "H5" || row.data.orderStatusCode === "H6") && haveInvoices) {
        return true;
      }
      return false;
    } else {
      if (row.data.orderStatusCode === "H5" || row.data.orderStatusCode === "H6") {
        return true;
      }
      return false;
    }
  };

  return (
    <Table className={classNames("scha__orders__overview-table", className)}>
      <TableHead>
        <TableRow>
          {ORDER_HISTORY_HEADER_DATA.map(renderHeaderCell)}
          {haveInvoices && (
            <TableCell style={{ padding: "10px" }}>
              <span className={"d-flex flex-direction-column f-bold"}>{t("orderListPage.table.header.invoices")}</span>
            </TableCell>
          )}
          <TableCell />
          <TableCell className={"scha__orders__overview-table__buttons"}>
            <LinkButton
              className={"scha__orders__overview-table__close-all-button"}
              data-test-id={"close-all-button"}
              onClick={toggleOpenAll}
            >
              {isAllOpen ? (
                <KeyboardArrowUpIcon  />
              ) : (
                <KeyboardArrowDownIcon  />
              )}
            </LinkButton>
          </TableCell>
        </TableRow>
      </TableHead>
      <TBody>
        {sortedRows &&
          sortedRows.map((row, index) => (
            <Fragment key={row.data.soNumber}>
              <TUpperRow
                className={classNames({
                  "scha__orders__overview-table__row": true,
                  open: row.open,
                })}
              >
                <TUpperCell className={getBodyCellCls(0)} data-test-id={`soNumber-${index}`}>
                  {row.data.soNumber}
                </TUpperCell>
                <TUpperCell className={getBodyCellCls(1)} data-test-id={`poNumber-${index}`}>
                  {row.data.poNumber}
                </TUpperCell>
                {isMultiAccountOrderSearchEnabled() && (
                  <TUpperCell className={getBodyCellCls(2)} data-test-id={`account-${index}`}>
                    <div>
                      {row.data.account?.shortCode}, {row.data.account?.name}
                    </div>
                    <div className={"mt-1 f-label-color"}>
                      {row.data.account?.endCustomerCode}, {row.data.account?.endCustomerName}
                    </div>
                  </TUpperCell>
                )}
                <TUpperCell
                  className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 3 : 2)}
                  data-test-id={`datePlaced-${index}`}
                >
                  {formatNullableDate(row.data.datePlaced, DateFormatType.DATE)}
                </TUpperCell>
                <TUpperCell
                  className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 4 : 3)}
                  data-test-id={`price-${index}`}
                >
                  <TotalPrice key={`price-${row.data.poNumber}`} price={PriceUtils.text(row.data.totalPrice)} />
                  {!!row.data.hasSurcharge && "*"}
                </TUpperCell>
                <TUpperCell
                  className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 5 : 4)}
                  data-test-id={`orderStatus-${index}`}
                >
                  <PopperTooltip
                    tooltipContent={<div style={{ width: 264 }}>{t("order.status.pr.hover")}</div>}
                    hideTooltip={!addStatusHover(row.data.orderStatusCode)}
                    placement={"bottom"}
                  >
                    <Chip data-test-id={"order-history-order-status"}>{t(row.data.orderStatus)}</Chip>
                  </PopperTooltip>
                </TUpperCell>
                {haveInvoices && (
                  <TUpperCell className={getBodyCellCls(isMultiAccountOrderSearchEnabled() ? 6 : 5)}>
                    {showDownloadPDF(row) && (
                      <>
                        <DownloadContentButton
                          labelHover={t("orderListPage.downloadInvoices.multiple.helperLabel")}
                          data-test-id={"order-history-order-details-button"}
                          key={`download-invoices-${row.data.poNumber}`}
                          label={t("downloadFile.type.pdf")}
                          onClick={() => handleDownload(row)}
                          isLoading={loading.id === row.data.soNumber && loading.state === true}
                        />
                      </>
                    )}
                  </TUpperCell>
                )}
                <TUpperCell className={getBodyCellCls(renderNumCells(5))}>
                  {!isErrorEntry(row) && (
                    <OrderDetails
                      key={`details-${row.data.poNumber}`}
                      link={row.data.detailsUrl}
                      label={t("orderDetailsPage.title")}
                    />
                  )}
                </TUpperCell>
                <TUpperCell className={getBodyCellCls(renderNumCells(6))}>
                  {!isErrorEntry(row) && (
                    <div className={"scha__orders__overview-table__open-close-button"}>
                      <Link onClick={() => toggleOpen(row)}>
                        {row.open ? (
                          <KeyboardArrowUpIcon  />
                        ) : (
                          <KeyboardArrowDownIcon  />
                        )}
                      </Link>
                    </div>
                  )}
                </TUpperCell>
              </TUpperRow>
              {row.open && row.loadingDetails && (
                <TableRow
                  className={classNames({
                    "scha__orders__overview-table__row": true,
                    "scha__orders__overview-table__spacer": true,
                    open: true,
                  })}
                >
                  <TCell colSpan={12}>
                    <Spinner />
                  </TCell>
                </TableRow>
              )}
              {row.open && !row.loadingDetails && (
                <>
                  <TableRow
                    className={classNames({
                      "scha__orders__overview-table__row": true,
                      "scha__orders__overview-table__details-row": true,
                      open: true,
                    })}
                  >
                    <TCell className={"scha__orders__overview-table__details-cell"} colSpan={renderNumCells(7)}>
                      <LineItemDetails data={row} isOpen={row.open}>
                        {renderDetails}
                      </LineItemDetails>
                    </TCell>
                  </TableRow>
                  <TableRow
                    className={classNames({
                      "scha__orders__overview-table__row": true,
                      "scha__orders__overview-table__spacer": true,
                      open: true,
                    })}
                  >
                    <TCell colSpan={7} />
                  </TableRow>
                </>
              )}
            </Fragment>
          ))}
      </TBody>
    </Table>
  );
};

const renderDetails = ({ data }: DocumentListRow<OrderData>) =>
  data.products && (
    <OrderLineTable data={data.products} orderData={data} soNumber={data.soNumber} poNumber={data.poNumber} />
  );

const OrderDetails = (props: { link: string; label: ReactNode }) => (
  <Link data-test-id={"order-history-order-details-button"} href={props.link}>
    {props.label}
  </Link>
);

const TotalPrice = (props: { price: string }) => <span className="f-bold">{props.price}</span>;

export const getBodyCellCls = (index: number, otherClasses?: string) => {
  const sortKind = ORDER_HISTORY_HEADER_DATA[index]?.sortKind ?? null;
  if (sortKind !== null) {
    return classNames("scha__orders__overview-table__sortable-cell", otherClasses);
  }
  return otherClasses;
};
